import type { MenuItemProps } from 'interfaces/navigation/Header';
import cx from 'classnames';
import { COMMON_TEST_IDS } from 'constants/testsIds/common';
import * as S from './NavigationStyles';
import SubMenuV1 from './SubmenuV1';

interface NavItemProps extends MenuItemProps {
  currentUrl: string;
  color: string;
  subdomain: string;
}

const NavItem = ({ currentUrl, color, url, label, subMenu, subdomain }: NavItemProps) => {
  const showSubmenu = subMenu && subMenu?.length > 0;
  const homepages = ['/', '/recommends/', '/crypto/', '/well/', '/asia/'];

  /**
   * Determines if the current URL exactly matches the target URL.
   * Handles paginated URLs by comparing the base URL without pagination.
   *
   * @param targetUrl - The URL to match against
   * @returns True if URLs match exactly, considering pagination
   */
  const isExactMatch = (targetUrl: string): boolean => {
    const pageIndex = currentUrl.indexOf('page');
    const hasPagination = pageIndex !== -1;
    const isHomepage = homepages.includes(targetUrl);

    // For non-homepage URLs without pagination, do direct comparison
    if (!isHomepage && !hasPagination) {
      return currentUrl === targetUrl;
    }

    // For paginated URLs or homepage URLs, compare base URL
    if (hasPagination) {
      const baseUrl = currentUrl.slice(0, pageIndex);
      return baseUrl === targetUrl;
    }

    // Direct comparison for non-paginated homepage URLs
    return currentUrl === targetUrl;
  };

  const isSubmenuSelected = () => {
    if (!showSubmenu) return false;
    return subMenu.some((item) => isExactMatch(item.url));
  };

  const isSelected = () => {
    return isExactMatch(url) || isSubmenuSelected();
  };
  const trackingData = {
    contentPlacementCD: 'topic navigation',
    eventAction: 'topic navigation',
    eventCategory: 'navigation',
  };

  const dataCyLabel = label.toLowerCase().split(' ').join('-');

  //this is done to remove focus from all links when submenu is opened
  const blurLinks = (e: React.MouseEvent) => {
    const target = e.currentTarget;
    const el = target.closest('.topic-navigation');
    const links = el?.querySelectorAll('a');
    links?.forEach((link) => link.blur());
  };

  return (
    <S.NavigationItem
      className={subdomain}
      $color={color}
      onMouseEnter={blurLinks}
      onMouseUp={blurLinks}
    >
      <S.NavigationStyledLink
        $color={subdomain === 'crypto' ? color : ''}
        href={url}
        ariaLabel={`Go to ${label}`}
        className={cx(subdomain, {
          selected: isSelected(),
          showSubmenu,
        })}
        tabIndex={0}
        trackingData={{ ...trackingData, eventAction: `${trackingData.eventAction} - level 0`, eventLabel: label }}
        type='topicNavigation'
        data-cy={dataCyLabel}
      >
        {label}
      </S.NavigationStyledLink>
      <S.SubMenuBridge />
      {showSubmenu && (
        <SubMenuV1
          subMenu={subMenu}
          parentLabel={label}
          trackingData={trackingData}
          dataCyLabel={`${COMMON_TEST_IDS.SUBMENU}-${dataCyLabel}`}
          onItemClick={blurLinks}
        />
      )}
    </S.NavigationItem>
  );
};

export default NavItem;
